import { useState } from 'react';
import {
  IDENTITY_PROVIDERS,
  TENANT_STATUSES,
  type CreateTenantInput,
  type IdentityProviderType,
  type TenantStatus,
  type UpdateTenantInput,
} from '../../../identity/api';
import { Select, TextField } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { useAsync } from '../../../components/common';

export type TenantFormValue = CreateTenantInput & { status?: TenantStatus };

export const PROVIDER_HELP: Record<IdentityProviderType, string> = {
  Workserver: 'Credentials are verified by the legacy Workserver at the identity provider URL.',
  Database:
    'AUTH keeps the users itself (Users tab). The URL is unused but still required — any URL will do.',
};

/** The tenant fields shared by create (no status) and edit (with status). */
export function TenantFields({
  value,
  onChange,
  withStatus,
  disabled,
}: {
  value: TenantFormValue;
  onChange: (v: TenantFormValue) => void;
  withStatus?: boolean;
  disabled?: boolean;
}) {
  const customers = useAsync(() => client.customers.list({ page: 1, pageSize: 100 }), []);
  const options = (customers.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }));
  if (value.customerId && !options.some((o) => o.value === value.customerId))
    options.push({ value: value.customerId, label: value.customerId });
  return (
    <div className="nyk-form">
      <TextField
        label="Name"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        maxLength={100}
        required
        disabled={disabled}
      />
      <Select
        label="Customer"
        value={value.customerId}
        onChange={(customerId) => onChange({ ...value, customerId })}
        options={options}
        placeholder={
          customers.loading ? 'Loading…' : options.length ? 'Select a customer…' : 'No customer yet'
        }
        required
        disabled={disabled}
        hint={!customers.loading && !options.length ? 'Create a customer first (Customers page).' : undefined}
      />
      <Select
        label="Identity provider"
        value={value.identityProvider}
        onChange={(v) => onChange({ ...value, identityProvider: v as IdentityProviderType })}
        options={IDENTITY_PROVIDERS.map((p) => ({ value: p, label: p }))}
        disabled={disabled}
        hint={PROVIDER_HELP[value.identityProvider]}
      />
      {withStatus && (
        <Select
          label="Status"
          value={value.status ?? 'Active'}
          onChange={(v) => onChange({ ...value, status: v as TenantStatus })}
          options={TENANT_STATUSES.map((s) => ({ value: s, label: s }))}
          disabled={disabled}
          hint="Suspended and Deleted tenants are refused at sign-in (403)."
        />
      )}
      <TextField
        label="Identity provider URL"
        type="url"
        value={value.identityProviderUrl}
        onChange={(e) => onChange({ ...value, identityProviderUrl: e.target.value })}
        placeholder="https://workserver.example.com/api/authenticate"
        required
        disabled={disabled}
        className="nyk-url"
      />
    </div>
  );
}

export function useTenantForm(initial: TenantFormValue) {
  const [value, setValue] = useState<TenantFormValue>(initial);
  const valid = !!value.name.trim() && !!value.customerId && !!value.identityProviderUrl.trim();
  return { value, setValue, valid };
}

export const toUpdate = (v: TenantFormValue): UpdateTenantInput => ({
  name: v.name.trim(),
  status: v.status ?? 'Active',
  identityProvider: v.identityProvider,
  identityProviderUrl: v.identityProviderUrl.trim(),
  customerId: v.customerId,
});

export const toCreate = (v: TenantFormValue): CreateTenantInput => {
  const { status: _status, ...rest } = toUpdate(v);
  return rest;
};
